import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Save, ClipboardList, Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * BaselineStaffEntryCard — quick FMS + GIRD baseline entry for the coach /
 * athletic-trainer / physical-therapist on the Player Detail page.
 *
 * This is the closing of the loop on the screening_assessment_mode toggle.
 * When the Head Coach selects "Local AT / PT staff" the athlete sees a
 * handoff message — their staff then enters the values via this form, which
 * POSTs to the same canonical screening endpoints (/screening/fms,
 * /screening/gird) the self-test wizards use. This guarantees one source of
 * truth for the player_profile + screening_events stream.
 */

const FMS_MOVEMENTS = [
  { key: "deep_squat",            label: "Deep Squat" },
  { key: "hurdle_step",           label: "Hurdle Step" },
  { key: "inline_lunge",          label: "Inline Lunge" },
  { key: "shoulder_mobility",     label: "Shoulder Mobility" },
  { key: "aslr",                  label: "Active Straight-Leg Raise" },
  { key: "trunk_stability_pushup", label: "Trunk Stability Pushup" },
  { key: "rotary_stability",      label: "Rotary Stability" },
];

export default function BaselineStaffEntryCard({ playerId, playerName, dominantArm = "right", onSaved }) {
  const [scores, setScores] = useState({});
  const [gird, setGird] = useState({
    ir_dominant: "", ir_non_dominant: "", er_dominant: "", er_non_dominant: "",
    dominant_arm: dominantArm,
  });
  const [savingFms, setSavingFms] = useState(false);
  const [savingGird, setSavingGird] = useState(false);
  const [screeningMode, setScreeningMode] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/teams/me/screening-mode");
        setScreeningMode(data.mode || "armiq_builtin");
      } catch { setScreeningMode("armiq_builtin"); }
    })();
  }, []);

  const fmsTotal = FMS_MOVEMENTS.reduce((sum, m) => sum + (Number(scores[m.key]) || 0), 0);
  const fmsComplete = FMS_MOVEMENTS.every((m) => scores[m.key] !== undefined && scores[m.key] !== "");

  async function submitFms() {
    if (!fmsComplete) { toast.error("Score all 7 movements first"); return; }
    setSavingFms(true);
    try {
      const payload = {
        ...Object.fromEntries(FMS_MOVEMENTS.map((m) => [m.key, Number(scores[m.key])])),
        method: "coach",
        player_user_id: playerId,
        notes: `Submitted on behalf of ${playerName || "athlete"} by staff.`,
      };
      await api.post("/screening/fms", payload);
      toast.success(`FMS submitted · total ${fmsTotal}/21`);
      onSaved?.("fms");
    } catch (e) { toast.error("Could not save FMS — check console"); }
    finally { setSavingFms(false); }
  }

  async function submitGird() {
    const irD = Number(gird.ir_dominant);
    const irN = Number(gird.ir_non_dominant);
    if (Number.isNaN(irD) || Number.isNaN(irN) || !gird.ir_dominant || !gird.ir_non_dominant) {
      toast.error("Internal rotation values required for both shoulders"); return;
    }
    setSavingGird(true);
    try {
      const payload = {
        ir_dominant: irD, ir_non_dominant: irN,
        er_dominant: gird.er_dominant === "" ? null : Number(gird.er_dominant),
        er_non_dominant: gird.er_non_dominant === "" ? null : Number(gird.er_non_dominant),
        dominant_arm: gird.dominant_arm,
        method: "manual",                           // AT/PT goniometer
        proxy_role: "coach",
        player_user_id: playerId,
      };
      const { data } = await api.post("/screening/gird", payload);
      const sev = data?.severity || "saved";
      toast.success(`GIRD saved · ${data?.gird_deg?.toFixed?.(1) || "—"}° (${sev})`);
      onSaved?.("gird");
    } catch (e) { toast.error("Could not save GIRD — check console"); }
    finally { setSavingGird(false); }
  }

  const localMode = screeningMode === "local_staff";

  return (
    <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-5 space-y-6" data-testid="baseline-staff-entry-card">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "var(--brand-secondary)" }}>
            <ClipboardList className="h-3 w-3" /> Staff baseline entry · 60-second form
          </div>
          <div className="font-display font-bold text-white text-lg mt-1">FMS &amp; GIRD for {playerName || "athlete"}</div>
          <div className="text-[11px] text-white/55 mt-0.5">
            {localMode
              ? "Local AT/PT mode is active. Enter the values your staff measured below."
              : "ArmIQ Functional Movement mode is active. Use this form when staff override the athlete's self-test."}
          </div>
        </div>
        {localMode && <span className="px-2 py-1 rounded text-[9px] uppercase tracking-wider font-bold border border-amber-400/40 text-amber-300 bg-amber-500/5" data-testid="baseline-local-staff-pill">Local AT/PT</span>}
      </div>

      {/* FMS section */}
      <div data-testid="baseline-fms-block">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-white/55"><Activity className="h-3 w-3 inline mr-1" />Functional Movement Screen</div>
          <div className="text-[11px] font-mono-num text-white/65">Total <b className="text-white">{fmsTotal}</b> / 21</div>
        </div>
        <div className="space-y-2">
          {FMS_MOVEMENTS.map((m) => (
            <div key={m.key} className="flex items-center justify-between gap-3 bg-black/30 border border-white/8 rounded-md px-3 py-2">
              <div className="text-sm text-white/85">{m.label}</div>
              <div className="flex gap-1">
                {[0, 1, 2, 3].map((v) => {
                  const active = Number(scores[m.key]) === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setScores({ ...scores, [m.key]: v })}
                      className="w-9 h-9 rounded text-sm font-mono-num font-bold transition-all"
                      style={{
                        backgroundColor: active ? "var(--brand-secondary)" : "rgba(255,255,255,0.05)",
                        color: active ? "#fff" : "rgba(255,255,255,0.65)",
                        border: `1px solid ${active ? "var(--brand-secondary)" : "rgba(255,255,255,0.1)"}`,
                      }}
                      data-testid={`baseline-fms-${m.key}-${v}`}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <Button onClick={submitFms} disabled={!fmsComplete || savingFms}
                className="w-full text-white red-glow mt-4"
                style={{ backgroundColor: "var(--brand-secondary)" }}
                data-testid="baseline-fms-submit">
          {savingFms ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Submit FMS · {fmsTotal}/21
        </Button>
      </div>

      {/* GIRD section */}
      <div className="border-t border-white/10 pt-5" data-testid="baseline-gird-block">
        <div className="text-[10px] uppercase tracking-wider font-bold text-white/55 mb-3">
          <Activity className="h-3 w-3 inline mr-1" /> Shoulder Range of Motion · GIRD assessment
        </div>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-[11px] text-white/55">Dominant arm</span>
          {["right", "left"].map((side) => (
            <button
              key={side}
              type="button"
              onClick={() => setGird({ ...gird, dominant_arm: side })}
              className="px-3 py-1 rounded text-[11px] font-bold transition-all"
              style={{
                backgroundColor: gird.dominant_arm === side ? "var(--brand-secondary)" : "rgba(255,255,255,0.05)",
                color: gird.dominant_arm === side ? "#fff" : "rgba(255,255,255,0.65)",
                border: `1px solid ${gird.dominant_arm === side ? "var(--brand-secondary)" : "rgba(255,255,255,0.1)"}`,
              }}
              data-testid={`baseline-gird-dominant-${side}`}
            >
              {side.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-2.5">
          {[
            { key: "ir_dominant",      label: "IR · dominant (deg)",     required: true },
            { key: "ir_non_dominant",  label: "IR · non-dominant (deg)", required: true },
            { key: "er_dominant",      label: "ER · dominant (deg)" },
            { key: "er_non_dominant",  label: "ER · non-dominant (deg)" },
          ].map((f) => (
            <label key={f.key} className="block">
              <span className="text-[11px] text-white/55 uppercase tracking-wider">{f.label}{f.required && <span className="text-red-300 ml-1">*</span>}</span>
              <input
                type="number" step="1" min="0" max="180"
                value={gird[f.key]}
                onChange={(e) => setGird({ ...gird, [f.key]: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-md bg-black/35 border border-white/10 text-white text-sm font-mono-num focus:outline-none focus:border-white/25"
                data-testid={`baseline-gird-${f.key}`}
                placeholder="—"
              />
            </label>
          ))}
        </div>
        <Button onClick={submitGird} disabled={savingGird}
                className="w-full text-white red-glow mt-4"
                style={{ backgroundColor: "var(--brand-secondary)" }}
                data-testid="baseline-gird-submit">
          {savingGird ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Submit GIRD measurement
        </Button>
      </div>
    </Card>
  );
}
